import { useState, useEffect } from 'react'
import {Play, Pause, Flame, Wind, Eye} from 'lucide-react'
import { getCachedText, preloadSiteContent } from '@/hooks/useSiteContent'

export default function Experimenter() {
  const [activeAudio, setActiveAudio] = useState<string | null>(null)
  const [, setTick] = useState(0)

  useEffect(() => {
    preloadSiteContent()
    const handler = () => setTick((n) => n + 1)
    window.addEventListener('site-content-updated', handler)
    return () => window.removeEventListener('site-content-updated', handler)
  }, [])

  const t = (key: string, fallback: string) => getCachedText('experimenter', key, fallback)

  const mantras = [
    t('mantra_1', 'Je suis ici, éveillé(e).'),
    t('mantra_2', 'Le corps est vivant. Je le laisse s\'ouvrir.'),
    t('mantra_3', 'Présent(e) à ce qui monte.'),
    t('mantra_4', 'La perception s\'élargit. Je reste là.'),
    t('mantra_5', 'L\'énergie circule. Je l\'accueille, sans fuir.')
  ]

  const audios = [
    {
      id: 'ancrage',
      duree: t('audio_1_duree', '3 min'),
      frequence: t('audio_1_frequence', '432 Hz'),
      titre: t('audio_1_titre', 'Ancrage vibratoire'),
      description: t('audio_1_desc', 'Une respiration qui ouvre. Le corps se remet en présence.'),
      icon: Wind,
    },
    {
      id: 'ouverture',
      duree: t('audio_2_duree', '12 min'),
      frequence: t('audio_2_frequence', '528 Hz'),
      titre: t('audio_2_titre', 'Ouverture des perceptions'),
      description: t('audio_2_desc', 'Yeux ouverts ou fermés. L\'énergie circule, la conscience s\'élargit.'),
      icon: Eye,
    },
    {
      id: 'activation',
      duree: t('audio_3_duree', '20 min'),
      frequence: t('audio_3_frequence', '8–12 Hz · alpha'),
      titre: t('audio_3_titre', 'Activation complète'),
      description: t('audio_3_desc', 'Une montée lente et lucide. Le corps devient un espace plus vaste.'),
      icon: Flame,
    }
  ]

  const experiences = [
    {
      titre: t('exp_1_titre', 'Voir sans nommer'),
      texte: t('exp_1_texte', 'Poser les yeux sur un objet. Le voir vraiment, sans étiquette, sans histoire. Sentir comment la perception s\'élargit dès que le mental cesse de commenter. Rester éveillé(e) dans ce qui s\'ouvre.')
    },
    {
      titre: t('exp_2_titre', 'Écouter l\'espace entre les sons'),
      texte: t('exp_2_texte', 'Yeux ouverts. Laisser les sons venir sans les chercher. Noter l\'espace vivant qui existe entre eux. Cet espace, c\'est toi — présent(e), conscient(e), ouvert(e).')
    },
    {
      titre: t('exp_3_titre', 'Sentir le corps vivant'),
      texte: t('exp_3_texte', 'Debout ou assis(e), dos droit. Sentir l\'énergie qui circule — pieds, ventre, poitrine, tête. Ne rien forcer, ne rien endormir. Juste remarquer que le corps est déjà là, vivant, disponible.')
    }
  ]

  return (
    <div className="fade-in">
      {/* Intro — activation */}
      <section className="px-6 py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs tracking-[0.4em] uppercase text-[#c9a96e] mb-8">
            {t('eyebrow', '— Deuxième porte —')}
          </p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-[#f5f1ea] mb-10">
            {t('page_title', 'Ressentir')}
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-10" />
          <p className="font-serif text-xl md:text-2xl text-[#f5f1ea]/85 leading-loose italic whitespace-pre-line">
            {t('page_intro', "Un espace où le corps s'ouvre,\noù la conscience reste éveillée.")}
          </p>
          <p className="text-sm text-[#a8a39e] font-light mt-8 tracking-wide">
            {t('page_sub', "Ici, on ne s'endort pas. On s'active.")}
          </p>
        </div>
      </section>

      {/* Mantras — activation */}
      <section className="px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/70 mb-4">{t('mantras_eyebrow', 'Ancrages vivants')}</p>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-[#f5f1ea]">
              {t('mantras_title', 'À poser, yeux ouverts')}
            </h2>
          </div>

          <div className="space-y-16 text-center">
            {mantras.map((mantra, i) => (
              <p
                key={i}
                className="font-serif text-2xl md:text-4xl italic text-[#f5f1ea]/85 leading-relaxed"
                style={{ animationDelay: `${i * 0.2}s` }}
              >
                « {mantra} »
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Audios — fréquences activantes */}
      <section className="px-6 py-32 border-t border-[#c9a96e]/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/70 mb-4">{t('audios_eyebrow', 'Fréquences')}</p>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-[#f5f1ea] mb-6">
              {t('audios_title', 'Écoutes vibratoires')}
            </h2>
            <p className="text-sm text-[#a8a39e] font-light max-w-lg mx-auto leading-relaxed whitespace-pre-line">
              {t('audios_desc', "Des fréquences pour activer, pas pour endormir.\nL'énergie circule. Tu restes présent(e).")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {audios.map((audio) => {
              const Icon = audio.icon
              return (
                <div
                  key={audio.id}
                  className="group relative border border-[#c9a96e]/20 hover:border-[#c9a96e]/60 transition-all duration-700 p-8 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-6 rounded-full border border-[#c9a96e]/40 flex items-center justify-center group-hover:border-[#c9a96e] transition-colors duration-700">
                    <Icon className="w-4 h-4 text-[#c9a96e]" />
                  </div>
                  <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-2">
                    {audio.duree}
                  </p>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#c9a96e]/60 mb-4">
                    {audio.frequence}
                  </p>
                  <h3 className="font-serif text-2xl text-[#f5f1ea] mb-4">
                    {audio.titre}
                  </h3>
                  <p className="text-sm text-[#a8a39e] font-light leading-relaxed mb-8 min-h-[3rem]">
                    {audio.description}
                  </p>
                  <button
                    onClick={() => setActiveAudio(activeAudio === audio.id ? null : audio.id)}
                    className="inline-flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-[#c9a96e] border-b border-[#c9a96e]/30 pb-2 hover:border-[#c9a96e] transition-all duration-500"
                    aria-label={activeAudio === audio.id ? 'Pause' : 'Activer'}
                  >
                    {activeAudio === audio.id ? (
                      <>
                        <Pause className="w-4 h-4" />
                        <span>En activation</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        <span>Activer</span>
                      </>
                    )}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Sensory experiences */}
      <section className="px-6 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/70 mb-4">{t('experiences_eyebrow', 'Expériences actives')}</p>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-[#f5f1ea]">
              {t('experiences_title', 'Petits gestes, grandes ouvertures')}
            </h2>
          </div>

          <div className="space-y-16">
            {experiences.map((exp, i) => (
              <div key={i} className="flex flex-col md:flex-row gap-8 items-start">
                <p className="font-serif text-5xl text-[#c9a96e]/30">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <div className="flex-1">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#f5f1ea] mb-4">
                    {exp.titre}
                  </h3>
                  <p className="text-base md:text-lg text-[#f5f1ea]/80 leading-loose font-light">
                    {exp.texte}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Texte vibratoire — activation, pas sommeil */}
      <section className="px-6 py-32 border-t border-[#c9a96e]/10">
        <div className="max-w-2xl mx-auto text-center space-y-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]">{t('closing_eyebrow', 'À poser en présence')}</p>
          <div className="space-y-8">
            <p className="font-serif italic text-xl md:text-2xl text-[#f5f1ea]/85 leading-loose">
              {t('closing_1', 'Respirer, éveillé(e).')}
            </p>
            <p className="font-serif italic text-xl md:text-2xl text-[#f5f1ea]/85 leading-loose">
              {t('closing_2', "Sentir l'air qui entre — l'énergie monte.")}
            </p>
            <p className="font-serif italic text-xl md:text-2xl text-[#f5f1ea]/85 leading-loose">
              {t('closing_3', "Sentir l'air qui sort — l'espace s'ouvre.")}
            </p>
            <p className="font-serif italic text-xl md:text-2xl text-[#c9a96e]/90 leading-loose">
              {t('closing_4', 'Rester là. Conscient(e). Vivant(e).')}
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}
